import { useCallback, useState } from "react";
import { useAppSelector } from "store/hooks";
import { companyMetadataSelector } from "store/selectors";

import { showErrorModal } from "helpers";
import {
  hasCompanyActiveFreeTrial,
  hasFreeTrialStarted,
  isSubscriptionPaymentFailed,
  isSubscriptionRenewalRequired,
} from "helpers/subscription";

import {
  mutationSubscriptionControllerEndFreeTrial,
  querySubscriptionControllerGetCustomerPortal,
} from "utils/swagger_react_query";

import useAuth from "./useAuth";

interface IUseSubscriptionParams {
  setIsLoading?: (state: boolean) => void;
  refetchData?: () => Promise<void>;
}

export const useSubscription = ({ setIsLoading, refetchData }: IUseSubscriptionParams) => {
  const { getCompanyInfo } = useAuth();
  const currentCompany = useAppSelector(companyMetadataSelector);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  const isFreeTrialFlow = useCallback(() => {
    return hasCompanyActiveFreeTrial(currentCompany);
  }, [currentCompany]);
  const isRenewalRequired = useCallback(() => {
    return isSubscriptionRenewalRequired(currentCompany);
  }, [currentCompany]);
  const isFreeTrialAvailable = useCallback(() => {
    return hasFreeTrialStarted(currentCompany);
  }, [currentCompany]);
  const isPaymentFailed = useCallback(() => {
    return isSubscriptionPaymentFailed(currentCompany);
  }, [currentCompany]);

  const shouldShowSubscriptionAlert = isFreeTrialFlow() || isPaymentFailed();

  const _getCurrentCompany = async () => {
    const company = await getCompanyInfo(currentCompany?.companyId || "", true);
    return company;
  };

  const handleManageSubscriptionPortal = useCallback(async () => {
    try {
      setIsLoading?.(true);
      const response = await querySubscriptionControllerGetCustomerPortal();
      setIsLoading?.(false);
      window.open(response.customerPortalUrl, "_blank");
    } catch (error) {
      showErrorModal(error);
    } finally {
      setIsLoading?.(false);
    }
  }, [currentCompany]);

  const handleCancelFreeTrial = useCallback(async () => {
    try {
      setIsLoading?.(true);
      await mutationSubscriptionControllerEndFreeTrial()();
      await _getCurrentCompany();
      await refetchData?.();
      setIsLoading?.(false);
    } catch (error) {
      showErrorModal(error);
    } finally {
      setIsLoading?.(false);
    }
  }, [currentCompany]);

  return {
    isFreeTrialFlow,
    isRenewalRequired,
    isFreeTrialAvailable,
    isPaymentFailed,
    shouldShowSubscriptionAlert,
    handleManageSubscriptionPortal,
    handleCancelFreeTrial,
    checkoutModal: {
      isOpen: isCheckoutModalOpen,
      onClose: () => setIsCheckoutModalOpen(false),
      onOpen: () => setIsCheckoutModalOpen(true),
    },
  };
};
